import React, { useState, useEffect, useContext, memo } from 'react';
import axios from 'axios';
import { AuthContext, API } from '../shared';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Search } from 'lucide-react';

const SimpleInstallmentsManagement = memo(() => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('receipts'); // receipts, create
  
  // States
  const [receipts, setReceipts] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  
  // Create receipt form
  const [receiptForm, setReceiptForm] = useState({
    client_id: '',
    service_id: '',
    total_amount: '',
    first_payment: '',
    number_of_payments: 3,
    payment_frequency: 'monthly'
  });
  
  // Payment tracking
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [receiptPayments, setReceiptPayments] = useState([]);
  const [showPaymentsDialog, setShowPaymentsDialog] = useState(false);
  const [showPaymentRecord, setShowPaymentRecord] = useState(false);
  const [selectedPaymentForRecord, setSelectedPaymentForRecord] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');

  // Reports states
  const [summaryReport, setSummaryReport] = useState(null);
  const [overdueReport, setOverdueReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  
  // NEW: Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // NEW: Edit due-date inline
  const [editingDueDateId, setEditingDueDateId] = useState(null);
  const [editingDueDateValue, setEditingDueDateValue] = useState('');

  // 🆕 2026-07-22: Admin settlement (تسوية إدارية) — closes a receipt
  // without deleting the client so it disappears from the "debtors" list.
  const isAdmin = user?.role === 'super_admin' || user?.role === 'general_accountant';
  const [settlingId, setSettlingId] = useState(null);

  const settleReceipt = async (receipt) => {
    const note = window.prompt(
      `⚠️ تسوية إدارية للوصل ${receipt.receipt_no}\n\n` +
      `العميل: ${receipt.client_name}\n` +
      `المبلغ الإجمالي: ${(receipt.total_amount || 0).toLocaleString()} دج\n\n` +
      `ستُغلق كل الدفعات المعلّقة ويخرج العميل من قائمة المدينين.\n` +
      `اكتب ملاحظة توضّح سبب التسوية (اختياري):`,
      '',
    );
    if (note === null) return; // cancelled
    setSettlingId(receipt.id);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(
        `${API}/installment-receipts/${receipt.id}/settle`,
        { settlement_note: note || null, settlement_kind: 'admin_settlement' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      alert(`✅ ${r.data.message}\nتمت تسوية ${r.data.settled_payments} دفعة معلّقة.`);
      // Optimistic update
      setReceipts(prev => prev.map(x => x.id === receipt.id
        ? { ...x, settled: true, settled_by_name: r.data.settled_by, settled_at: r.data.settled_at }
        : x));
      if (searchResults.length > 0) {
        setSearchResults(prev => prev.map(x => x.id === receipt.id
          ? { ...x, settled: true, settled_by_name: r.data.settled_by, settled_at: r.data.settled_at }
          : x));
      }
    } catch (e) {
      alert('❌ ' + (e.response?.data?.detail || e.message));
    } finally {
      setSettlingId(null);
    }
  };

  const unsettleReceipt = async (receipt) => {
    if (!window.confirm(`إلغاء التسوية على الوصل ${receipt.receipt_no}؟ ستعود الدفعات إلى حالتها السابقة.`)) return;
    setSettlingId(receipt.id);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/installment-receipts/${receipt.id}/unsettle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setReceipts(prev => prev.map(x => x.id === receipt.id
        ? { ...x, settled: false, settled_by_name: null, settled_at: null }
        : x));
      if (searchResults.length > 0) {
        setSearchResults(prev => prev.map(x => x.id === receipt.id
          ? { ...x, settled: false, settled_by_name: null, settled_at: null }
          : x));
      }
    } catch (e) {
      alert('❌ ' + (e.response?.data?.detail || e.message));
    } finally {
      setSettlingId(null);
    }
  };

  const saveDueDate = async (payment) => {
    if (!editingDueDateValue) {
      alert('الرجاء إدخال تاريخ صالح');
      return;
    }
    try {
      await axios.put(
        `${API}/installment-receipts/${payment.receipt_id}/payments/${payment.id}/due-date`,
        { due_date: editingDueDateValue }
      );
      setEditingDueDateId(null);
      setEditingDueDateValue('');
      // Refresh payments list of the open receipt
      if (selectedReceipt) {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/installment-receipts/${selectedReceipt.id}/payments`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setReceiptPayments(r.data?.payments || r.data || []);
      }
    } catch (e) {
      alert('❌ ' + (e.response?.data?.detail || e.message));
    }
  };

  // Fetch data
  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Get all required data
      const [receiptsRes, clientsRes, servicesRes] = await Promise.all([
        axios.get(`${API}/installment-receipts`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(err => {
          console.error('Error fetching receipts:', err);
          return { data: [] };
        }),
        axios.get(`${API}/clients`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(err => {
          console.error('Error fetching clients:', err);
          return { data: [] };
        }),
        axios.get(`${API}/services`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(err => {
          console.error('Error fetching services:', err);
          return { data: [] };
        })
      ]);
      
      console.log('Installment data fetched:');
      console.log('- Receipts:', receiptsRes.data?.length || 0);
      console.log('- Clients:', clientsRes.data?.length || 0);
      console.log('- Services:', servicesRes.data?.length || 0);
      
      setReceipts(receiptsRes.data || []);
      setClients(clientsRes.data || []);
      setServices(servicesRes.data || []);
      
    } catch (error) {
      console.error('⚠️ تعذر جلب بيانات المدفوعات:', error);
      alert('⚠️ تعذر جلب بيانات المدفوعات، سيتم استخدام القيم الافتراضية');
      
      // Set default empty arrays
      setReceipts([]);
      setClients([]);
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  // NEW: Search function
  const searchReceipts = async () => {
    if (!searchQuery.trim()) {
      alert('يرجى إدخال رقم الوصل للبحث');
      return;
    }

    setIsSearching(true);
    try {
      // Search in current receipts
      const filteredReceipts = receipts.filter(receipt => 
        receipt.receipt_no?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        receipt.client_name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      
      setSearchResults(filteredReceipts);
      
      if (filteredReceipts.length === 0) {
        alert('لم يتم العثور على وصولات تطابق البحث');
      }
    } catch (error) {
      console.error('Error searching receipts:', error);
      alert('فشل في البحث');
    } finally {
      setIsSearching(false);
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
  };

  // Fetch reports
  const fetchReports = async () => {
    setReportLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Get summary report
      const summaryResponse = await axios.get(`${API}/installment-reports/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummaryReport(summaryResponse.data);
      
      // Get overdue agencies report (for managers/accountants)
      if (user?.role === 'general_accountant' || user?.role === 'super_admin') {
        const overdueResponse = await axios.get(`${API}/installment-reports/overdue-agencies`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOverdueReport(overdueResponse.data);
      }
      
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setReportLoading(false);
    }
  };

  // Transfer installments to treasury
  const transferToTreasury = async (paymentIds) => {
    if (!paymentIds || paymentIds.length === 0) {
      alert('يرجى اختيار الأقساط المراد تحويلها');
      return;
    }

    if (!confirm('هل تريد تحويل الأقساط المختارة إلى الخزينة العامة؟')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/agency-installment-transfers`, paymentIds, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ تم تحويل الأقساط إلى الخزينة العامة بنجاح');
      
      // Refresh data
      await fetchData();
      if (selectedReceipt) {
        await viewReceiptPayments(selectedReceipt);
      }
    } catch (error) {
      console.error('Error transferring to treasury:', error);
      alert('❌ فشل في التحويل: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Create receipt
  const createReceipt = async () => {
    if (!receiptForm.client_id || !receiptForm.service_id || !receiptForm.total_amount || !receiptForm.first_payment) {
      alert('يرجى إدخال جميع البيانات المطلوبة');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/installment-receipts`, receiptForm, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ تم إنشاء وصل التقسيط بنجاح');
      setReceiptForm({
        client_id: '',
        service_id: '',
        total_amount: '',
        first_payment: '',
        number_of_payments: 3,
        payment_frequency: 'monthly'
      });
      
      fetchData();
      setActiveTab('receipts');
    } catch (error) {
      console.error('Error creating receipt:', error);
      alert('❌ فشل في إنشاء الوصل: ' + (error.response?.data?.detail || error.message));
    }
  };

  // View receipt payments
  const viewReceiptPayments = async (receipt) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/installment-receipts/${receipt.id}/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedReceipt(receipt);
      setReceiptPayments(response.data.payments);
      setShowPaymentsDialog(true);
    } catch (error) {
      console.error('Error fetching receipt payments:', error);
      alert('فشل في تحميل دفعات الوصل');
    }
  };

  // Record payment
  const recordPayment = async () => {
    if (!paymentAmount || !selectedPaymentForRecord) {
      alert('يرجى إدخال مبلغ الدفعة');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/installment-receipts/${selectedReceipt.id}/payments/${selectedPaymentForRecord.id}/pay`, 
        {
          payment_id: selectedPaymentForRecord.id,
          amount_paid: parseFloat(paymentAmount)
        }, 
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      alert('✅ تم تسجيل الدفعة في العمليات اليومية');
      setShowPaymentRecord(false);
      setPaymentAmount('');
      
      // Refresh payments
      await viewReceiptPayments(selectedReceipt);
    } catch (error) {
      console.error('Error recording payment:', error);
      alert('❌ فشل في تسجيل الدفعة: ' + (error.response?.data?.detail || error.message));
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">📋 نظام الأقساط</h1>
          <p className="text-sm text-gray-600 mt-1">إدارة أقساط الخدمات بطريقة مبسطة</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('receipts')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'receipts'
                ? 'border-brand-500 text-brand-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            📋 الوصولات
          </button>
          <button
            onClick={() => setActiveTab('create')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'create'
                ? 'border-brand-500 text-brand-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            ➕ إضافة وصل جديد
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'reports'
                ? 'border-brand-500 text-brand-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            📊 التقارير
          </button>
        </nav>
      </div>

      {/* Receipts Tab */}
      {activeTab === 'receipts' && (
        <Card>
          <CardHeader>
            <CardTitle>📋 وصولات التقسيط</CardTitle>
            <CardDescription>
              جميع وصولات التقسيط مع إمكانية البحث برقم الوصل
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* NEW: Search Section */}
            <div className="bg-brand-50 border border-brand-200 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="🔍 ابحث برقم الوصل أو اسم العميل (مثال: INST-20250920-0001)"
                    onKeyPress={(e) => e.key === 'Enter' && searchReceipts()}
                  />
                </div>
                <Button
                  onClick={searchReceipts}
                  disabled={isSearching || !searchQuery.trim()}
                  className="bg-brand-600 hover:bg-brand-700"
                >
                  {isSearching ? 'جاري البحث...' : '🔍 بحث'}
                </Button>
                {searchResults.length > 0 && (
                  <Button
                    variant="outline"
                    onClick={clearSearch}
                    className="text-gray-600"
                  >
                    ✕ مسح
                  </Button>
                )}
              </div>
              
              {searchResults.length > 0 && (
                <div className="mt-3 text-sm text-brand-600">
                  📋 عُثر على {searchResults.length} وصل مطابق للبحث
                </div>
              )}
            </div>

            {/* Results */}
            {(searchResults.length > 0 ? searchResults : receipts).length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>لا توجد وصولات تقسيط حالياً</p>
                <p className="text-sm">اضغط "إضافة وصل جديد" لإنشاء وصل تقسيط</p>
              </div>
            ) : (
              <div className="space-y-4">
                {(searchResults.length > 0 ? searchResults : receipts).map((receipt) => (
                  <div key={receipt.id} className={`border rounded-lg p-4 hover:bg-gray-50 ${receipt.settled ? 'border-gray-300 bg-gray-50/70 opacity-90' : 'border-gray-200'}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-bold text-lg">{receipt.receipt_no}</h4>
                          {receipt.settled && (
                            <span
                              className="text-xs px-2 py-0.5 rounded-md bg-gray-200 text-gray-800 border border-gray-400 font-bold"
                              title={`تمت التسوية بواسطة ${receipt.settled_by_name || '—'}`}
                              data-testid={`receipt-settled-badge-${receipt.id}`}
                            >
                              🔒 تمت التسوية
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">
                          👤 {receipt.client_name} | 🎫 {receipt.service_name}
                        </p>
                        <p className="text-sm text-gray-600">
                          💰 {receipt.total_amount?.toLocaleString()} دج | 📊 {receipt.number_of_payments} دفعات
                        </p>
                      </div>
                      <div className="text-right flex flex-col gap-2 items-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => viewReceiptPayments(receipt)}
                          data-testid={`view-receipt-payments-${receipt.id}`}
                        >
                          👁️ عرض الدفعات
                        </Button>
                        {isAdmin && !receipt.settled && (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={settlingId === receipt.id}
                            onClick={() => settleReceipt(receipt)}
                            className="text-purple-700 border-purple-300 hover:bg-purple-50"
                            title="تسوية إدارية — يُخرج العميل من قائمة المدينين"
                            data-testid={`settle-receipt-${receipt.id}`}
                          >
                            {settlingId === receipt.id ? '⏳ جارٍ...' : '⚖️ تسوية'}
                          </Button>
                        )}
                        {isAdmin && receipt.settled && (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={settlingId === receipt.id}
                            onClick={() => unsettleReceipt(receipt)}
                            className="text-orange-700 border-orange-300 hover:bg-orange-50"
                            title="إلغاء التسوية"
                            data-testid={`unsettle-receipt-${receipt.id}`}
                          >
                            {settlingId === receipt.id ? '⏳ جارٍ...' : '↩️ إلغاء التسوية'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create Tab */}
      {activeTab === 'create' && (
        <Card>
          <CardHeader>
            <CardTitle>➕ إنشاء وصل تقسيط جديد</CardTitle>
            <CardDescription>الخطوة 1: إنشاء الوصل حسب المتطلبات</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Client Selection */}
              <div>
                <Label>العميل *</Label>
                <select
                  value={receiptForm.client_id}
                  onChange={(e) => setReceiptForm({...receiptForm, client_id: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="">اختر العميل</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      👤 {client.name} - {client.phone}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Service Selection */}
              <div>
                <Label>الخدمة *</Label>
                <select
                  value={receiptForm.service_id}
                  onChange={(e) => {
                    const service = services.find(s => s.id === e.target.value);
                    setReceiptForm({
                      ...receiptForm, 
                      service_id: e.target.value,
                      total_amount: service?.base_price?.toString() || ''
                    });
                  }}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="">اختر الخدمة</option>
                  {services.map((service) => (
                    <option key={service.id} value={service.id}>
                      🎫 {service.name} - {service.base_price?.toLocaleString()} دج
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Total Amount */}
              <div>
                <Label>المبلغ الإجمالي (دج) *</Label>
                <Input
                  type="number"
                  value={receiptForm.total_amount}
                  onChange={(e) => setReceiptForm({...receiptForm, total_amount: e.target.value})}
                  placeholder="المبلغ الإجمالي للخدمة"
                />
              </div>
              
              {/* First Payment */}
              <div>
                <Label>قيمة الدفعة الأولى (دج) *</Label>
                <Input
                  type="number"
                  value={receiptForm.first_payment}
                  onChange={(e) => setReceiptForm({...receiptForm, first_payment: e.target.value})}
                  placeholder="مبلغ الدفعة الأولى"
                />
              </div>
              
              {/* Number of Payments */}
              <div>
                <Label>عدد الدفعات *</Label>
                <Input
                  type="number"
                  value={receiptForm.number_of_payments}
                  onChange={(e) => setReceiptForm({...receiptForm, number_of_payments: parseInt(e.target.value) || 3})}
                  min="2"
                  max="12"
                />
              </div>
              
              {/* Payment Frequency */}
              <div>
                <Label>المدة الزمنية بين الدفعات *</Label>
                <select
                  value={receiptForm.payment_frequency}
                  onChange={(e) => setReceiptForm({...receiptForm, payment_frequency: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="weekly">أسبوعي</option>
                  <option value="monthly">شهري</option>
                  <option value="quarterly">ربع سنوي</option>
                </select>
              </div>
            </div>
            
            {/* Summary */}
            {receiptForm.total_amount && receiptForm.first_payment && receiptForm.number_of_payments && (
              <div className="bg-brand-50 p-4 rounded-lg">
                <h4 className="font-bold text-brand-800 mb-2">ملخص خطة التقسيط:</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">الدفعة الأولى:</span>
                    <div className="font-bold text-green-600">{parseFloat(receiptForm.first_payment || 0).toLocaleString()} دج</div>
                  </div>
                  <div>
                    <span className="text-gray-600">باقي المبلغ:</span>
                    <div className="font-bold text-orange-600">
                      {(parseFloat(receiptForm.total_amount || 0) - parseFloat(receiptForm.first_payment || 0)).toLocaleString()} دج
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600">قيمة كل قسط:</span>
                    <div className="font-bold text-brand-600">
                      {(((parseFloat(receiptForm.total_amount || 0) - parseFloat(receiptForm.first_payment || 0)) / (receiptForm.number_of_payments - 1)) || 0).toLocaleString()} دج
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex justify-end">
              <Button 
                onClick={createReceipt}
                className="bg-green-600 hover:bg-green-700"
                disabled={!receiptForm.client_id || !receiptForm.service_id || !receiptForm.total_amount || !receiptForm.first_payment}
              >
                📋 إنشاء وصل التقسيط
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reports Tab */}
      {activeTab === 'reports' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold">📊 تقارير الأقساط</h2>
            <Button onClick={fetchReports} disabled={reportLoading}>
              {reportLoading ? 'جاري التحميل...' : '🔄 تحديث التقارير'}
            </Button>
          </div>

          {/* Summary Cards */}
          {summaryReport && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-brand-600">
                      {summaryReport.summary?.total_installments || 0}
                    </div>
                    <div className="text-sm text-gray-600">إجمالي الأقساط</div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {summaryReport.summary?.total_paid?.toLocaleString() || 0}
                    </div>
                    <div className="text-sm text-gray-600">المبالغ المدفوعة (دج)</div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">
                      {summaryReport.summary?.total_remaining?.toLocaleString() || 0}
                    </div>
                    <div className="text-sm text-gray-600">المبالغ المتبقية (دج)</div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">
                      {summaryReport.summary?.overdue_installments || 0}
                    </div>
                    <div className="text-sm text-gray-600">أقساط متأخرة</div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Overdue Agencies (للمدير والمحاسب فقط) */}
          {(user?.role === 'general_accountant' || user?.role === 'super_admin') && overdueReport && (
            <Card>
              <CardHeader>
                <CardTitle className="text-red-600">🚨 الوكالات المتأخرة في التحويل (أكثر من 7 أيام)</CardTitle>
              </CardHeader>
              <CardContent>
                {Object.keys(overdueReport.overdue_agencies || {}).length === 0 ? (
                  <p className="text-center text-green-600 py-4">✅ لا توجد وكالات متأخرة</p>
                ) : (
                  <div className="space-y-3">
                    {Object.entries(overdueReport.overdue_agencies).map(([agencyId, data]) => (
                      <div key={agencyId} className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className="font-bold text-red-800">{data.agency_name}</h4>
                            <p className="text-sm text-red-600">
                              🚨 متأخرة {data.days_overdue} أيام | 💰 {data.overdue_amount?.toLocaleString()} دج
                            </p>
                          </div>
                          <div className="text-right">
                            <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs">
                              {data.overdue_payments} قسط متأخر
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Agency Summary */}
          {summaryReport?.by_agency && (
            <Card>
              <CardHeader>
                <CardTitle>📊 ملخص الوكالات</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(summaryReport.by_agency).map(([agencyId, data]) => (
                    <div key={agencyId} className="bg-gray-50 border rounded-lg p-3">
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="font-medium">{data.agency_name}</h4>
                          <p className="text-sm text-gray-600">
                            📋 {data.total_receipts} وصل | 💰 {data.total_amount?.toLocaleString()} دج
                          </p>
                        </div>
                        <div className="text-right text-sm">
                          <div className="text-green-600">مدفوع: {data.paid_amount?.toLocaleString()} دج</div>
                          <div className="text-orange-600">متبقي: {data.remaining_amount?.toLocaleString()} دج</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Payments Dialog */}
      <Dialog open={showPaymentsDialog} onOpenChange={setShowPaymentsDialog}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              📋 دفعات الوصل: {selectedReceipt?.receipt_no}
            </DialogTitle>
            <DialogDescription>
              الخطوة 2: متابعة خطة التقسيط
            </DialogDescription>
          </DialogHeader>
          
          {selectedReceipt && (
            <div className="space-y-4">
              {/* Receipt Summary */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><strong>العميل:</strong> {selectedReceipt.client_name}</div>
                  <div><strong>الخدمة:</strong> {selectedReceipt.service_name}</div>
                  <div><strong>المبلغ الإجمالي:</strong> {selectedReceipt.total_amount?.toLocaleString()} دج</div>
                  <div><strong>عدد الدفعات:</strong> {selectedReceipt.number_of_payments}</div>
                </div>
              </div>

              {/* NEW: Financial Summary */}
              <div className="bg-brand-50 p-4 rounded-lg border border-brand-200">
                <h4 className="font-bold text-brand-800 mb-3">💰 الملخص المالي للوصل</h4>
                <div className="grid grid-cols-3 gap-4">
                  {(() => {
                    const totalPaid = receiptPayments.reduce((sum, payment) => sum + (payment.amount_paid || 0), 0);
                    const remaining = (selectedReceipt.total_amount || 0) - totalPaid;
                    const completionPercentage = selectedReceipt.total_amount > 0 ? 
                      Math.round((totalPaid / selectedReceipt.total_amount) * 100) : 0;
                    
                    return (
                      <>
                        <div className="text-center">
                          <div className="text-lg font-bold text-green-600">
                            {totalPaid.toLocaleString()} دج
                          </div>
                          <div className="text-xs text-green-700">المبلغ المدفوع</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-orange-600">
                            {remaining.toLocaleString()} دج
                          </div>
                          <div className="text-xs text-orange-700">المبلغ المتبقي</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-brand-600">
                            {completionPercentage}%
                          </div>
                          <div className="text-xs text-brand-700">نسبة الإنجاز</div>
                        </div>
                      </>
                    );
                  })()}
                </div>
                
                {/* Progress Bar */}
                <div className="mt-3">
                  <div className="bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-brand-600 h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${selectedReceipt.total_amount > 0 ? 
                          Math.round((receiptPayments.reduce((sum, payment) => sum + (payment.amount_paid || 0), 0) / selectedReceipt.total_amount) * 100) : 0}%`
                      }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Payments Table */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-right">اختيار</TableHead>
                    <TableHead className="text-right">رقم القسط</TableHead>
                    <TableHead className="text-right">المبلغ المستحق</TableHead>
                    <TableHead className="text-right">تاريخ الاستحقاق</TableHead>
                    <TableHead className="text-right">المبلغ المدفوع</TableHead>
                    <TableHead className="text-right">حالة الدفع</TableHead>
                    <TableHead className="text-right">الإجراءات</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {receiptPayments.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell className="text-right">
                        {payment.status === 'paid' && !payment.transferred_to_treasury && (
                          <input 
                            type="checkbox" 
                            className="installment-transfer-checkbox"
                            data-payment-id={payment.id}
                          />
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {payment.payment_number}
                      </TableCell>
                      <TableCell className="text-right">
                        {payment.amount_due?.toLocaleString()} دج
                      </TableCell>
                      <TableCell className="text-right">
                        {editingDueDateId === payment.id ? (
                          <div className="flex items-center gap-1">
                            <Input
                              type="date"
                              value={editingDueDateValue}
                              onChange={(e) => setEditingDueDateValue(e.target.value)}
                              className="h-7 text-xs w-36"
                              data-testid={`edit-due-date-input-${payment.id}`}
                            />
                            <button
                              onClick={() => saveDueDate(payment)}
                              className="text-green-600 hover:text-green-700 text-sm"
                              title="حفظ"
                              data-testid={`save-due-date-${payment.id}`}
                            >✓</button>
                            <button
                              onClick={() => { setEditingDueDateId(null); setEditingDueDateValue(''); }}
                              className="text-red-500 hover:text-red-600 text-sm"
                              title="إلغاء"
                            >✕</button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            <span>{new Date(payment.due_date).toLocaleDateString('ar-DZ')}</span>
                            {payment.status !== 'paid' && (
                              <button
                                onClick={() => {
                                  setEditingDueDateId(payment.id);
                                  setEditingDueDateValue(payment.due_date ? new Date(payment.due_date).toISOString().split('T')[0] : '');
                                }}
                                className="text-brand-500 hover:text-brand-700 text-xs"
                                title="تعديل تاريخ الاستحقاق"
                                data-testid={`edit-due-date-btn-${payment.id}`}
                              >✏️</button>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={payment.amount_paid > 0 ? 'text-green-600 font-bold' : 'text-gray-400'}>
                          {payment.amount_paid?.toLocaleString()} دج
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={`px-2 py-1 rounded text-xs ${
                          payment.status === 'paid' ? 'bg-green-100 text-green-800' :
                          payment.status === 'overdue' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {payment.status === 'paid' ? '✅ مدفوع' :
                           payment.status === 'overdue' ? '🔴 متأخر' : '⏳ معلق'}
                        </span>
                        {payment.transferred_to_treasury && (
                          <span className="bg-brand-100 text-brand-800 px-2 py-1 rounded text-xs ml-1">
                            🏦 محول للخزينة
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {payment.status !== 'paid' && (
                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedPaymentForRecord(payment);
                              setPaymentAmount(payment.amount_due?.toString() || '');
                              setShowPaymentRecord(true);
                            }}
                            className="bg-brand-600 hover:bg-brand-700"
                          >
                            💰 دفع
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* الخطوة 4: زر التحويل للخزينة */}
              <div className="flex justify-between items-center mt-4 p-4 bg-purple-50 rounded-lg">
                <div>
                  <h4 className="font-bold text-purple-800">الخطوة 4: تحويل للخزينة العامة</h4>
                  <p className="text-sm text-purple-600">اختر الأقساط المدفوعة لتحويلها للخزينة</p>
                </div>
                <Button
                  onClick={() => {
                    const checkboxes = document.querySelectorAll('.installment-transfer-checkbox:checked');
                    const paymentIds = Array.from(checkboxes).map(cb => cb.getAttribute('data-payment-id'));
                    transferToTreasury(paymentIds);
                  }}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  🏦 تحويل المختار للخزينة
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Record Dialog */}
      <Dialog open={showPaymentRecord} onOpenChange={setShowPaymentRecord}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>💰 تسجيل دفع قسط</DialogTitle>
            <DialogDescription>
              الخطوة 3: تسجيل دفع قسط (سيُضاف للعمليات اليومية)
            </DialogDescription>
          </DialogHeader>
          
          {selectedPaymentForRecord && (
            <div className="space-y-4">
              <div className="bg-brand-50 p-3 rounded-lg">
                <p><strong>القسط رقم:</strong> {selectedPaymentForRecord.payment_number}</p>
                <p><strong>المبلغ المستحق:</strong> {selectedPaymentForRecord.amount_due?.toLocaleString()} دج</p>
                <p><strong>تاريخ الاستحقاق:</strong> {new Date(selectedPaymentForRecord.due_date).toLocaleDateString('ar-DZ')}</p>
              </div>
              
              <div>
                <Label>المبلغ المدفوع (دج) *</Label>
                <Input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  placeholder="أدخل المبلغ المدفوع"
                />
                <p className="text-xs text-gray-500 mt-1">
                  💡 يمكن دفع أكثر أو أقل من المستحق - سيُسجل الفرق
                </p>
              </div>
              
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setShowPaymentRecord(false)}>
                  إلغاء
                </Button>
                <Button onClick={recordPayment}>
                  💰 تسجيل الدفعة
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
});

export default SimpleInstallmentsManagement;
